/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 12/24/2019
 *
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";

/**
 * HOC for the partner form
 */
class PartnerForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            // main partner fields
            partnerId: null,
            partnerName: '',
            // the disabled url
            disabledUrl: '',
            // values for enabled dropdown
            enabled: '',
            enabledName: '',
            // the mac key field
            macKey: '',
            // sso site id
            ssoSiteId: '',
            // error message container
            isDisplayLoader: true,
            errors: {},
            // user token
            token: '',
            // form fields
            fields: [
                {
                    inputType: 'text',
                    labelName: 'Name *',
                    inputName: 'partnerName',
                    placeholder: 'Please enter partner name here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'textUrl',
                    labelName: 'Disabled Url *',
                    inputName: 'disabledUrl',
                    placeholder: 'Please enter Disabled Url here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Enabled *',
                    // initial attribute (the value name of the selected option in the dropdown)
                    inputName: 'enabled',
                    optionValueName: 'enabled',
                    optionLabelName: 'enabledName',
                    required: true,
                    path: null,
                    valueType: 'basic'
                },
                {
                    inputType: 'text',
                    labelName: 'SSO Site Id *',
                    inputName: 'ssoSiteId',
                    placeholder: 'Please enter SSO Site Id here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'text',
                    labelName: 'Mac Key',
                    inputName: 'macKey',
                    placeholder: 'Please enter Mac Key here',
                    required: true,
                    disabled: false
                }
            ]
        };

        // get the parameter from URL
        const partnerId = this.props.match.params.id;

        // set icons to the header
        if (partnerId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partners',
                pageName: "Partners",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partners",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'partners',
                pageName: "Partners",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/partners",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deletePartner);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    componentDidMount = async () => {
        // get parameter from URL
        const partnerId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (partnerId !== undefined) {
            const url = '/partners?search=%7BpartnerId:' + partnerId + '%7D';

            await API.get(url, {headers: {Authorization: token}}).then(res => {
                const data = res.data[0];
                oldState.partnerId = (data.partnerId !== undefined) ? data.partnerId : null;
                oldState.disabledUrl = (data.disabledUrl !== undefined) ? data.disabledUrl : '';
                oldState.enabled = (data.enabled !== undefined) ? data.enabled : '';
                oldState.macKey = (data.macKey !== undefined) ? data.macKey : '';
                oldState.partnerName = (data.partnerName !== undefined) ? data.partnerName : '';
                oldState.ssoSiteId = (data.ssoSiteId !== undefined) ? data.ssoSiteId : '' ;
                oldState.token = token;
                oldState.isDisplayLoader = false;

                this.setState(oldState);
            }).catch(er => {
                oldState.isDisplayLoader = false;
                this.setState(oldState);
                this.displayErrorsForAPICalls(er);
            });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deletePartner);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    handleSubmit = () => {
        let macKey = (this.state.macKey === '') ? null : this.state.macKey;

        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.partnerId !== null) {
            data = {
                partnerId: this.state.partnerId,
                disabledUrl: this.state.disabledUrl,
                enabled: Boolean(this.state.enabled),
                macKey: macKey,
                partnerName: this.state.partnerName,
                ssoSiteId: this.state.ssoSiteId
            };
        } else {
            data = {
                disabledUrl: this.state.disabledUrl,
                enabled: Boolean(this.state.enabled),
                macKey: macKey,
                partnerName: this.state.partnerName,
                ssoSiteId: Number(this.state.ssoSiteId)
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.partnerId !== null) {
                this.updatePartner(data);
            } else {
                this.createPartner(data);
            }
        }
    };

    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'partnerName':
                if (value === '')
                    error[key] = "Partner Name is Required!";
                else if (value === ' ')
                    error[key] = "Partner Name can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 32))
                    error[key] = "Partner Name can't have more than 32 characters!";
                else
                    error[key] = "";
                break;
            case 'disabledUrl':
                if (value === '')
                    error[key] = "Disabled URL is Required!";
                else if (value === ' ')
                    error[key] = "Disabled URL can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Disabled URL can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "Disabled URL must begin with with http://, https:// or / ";
                break;
            case 'enabled':
                // if (this.state.enabled === '')
                //     error[key] = "Please select an enabled!";
                // else
                    error[key] = "";
                break;
            case 'ssoSiteId':
                if (value === '' || value === 0)
                    error[key] = "SSO Site Id is Required! (If not enter 0)";
                else if (value === ' ')
                    error[key] = "SSO Site Id can't be an empty space!";
                else if(isNaN(value))
                    error[key] = "SSO Site Id need to be a number!";
                else if(isNaN(value) || value > 2147483647)
                    error[key] = "Please Enter a valid SSO Site Id!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    createPartner = async (data) => {
        await API.post('/partners',
            {
                "partnerName": data.partnerName,
                "enabled": data.enabled,
                "disabledUrl": data.disabledUrl,
                "ssoSiteId": data.ssoSiteId,
                "macKey": data.macKey
            }, {headers: {Authorization: this.state.token}}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the partner");
            this.props.history.push('/home/partners');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    updatePartner = async (data) => {
        console.log(EventEmitter.listeners);
        await API.put('/partners',
            {
                "partnerId": data.partnerId,
                "partnerName": data.partnerName,
                "enabled": data.enabled,
                "disabledUrl": data.disabledUrl,
                "ssoSiteId": data.ssoSiteId,
                "macKey": data.macKey
            },{headers: {Authorization: this.state.token}}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the partner");
            this.props.history.push('/home/partners');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    displayDeleteWarningMessage = () => {
        if (this.state.partnerId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'partners',
                name: this.state.partnerName
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the partner",
                alertTitle: "Error"
            });
        }

    };

    deletePartner = async (data) => {
        if (data && (this.props.match.path === "/home/partners/:id")) {
            await API.delete('/partners?partnerId=' + this.state.partnerId, {headers: {Authorization: this.state.token}})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the partner");
                    this.props.history.push('/home/partners');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Partner is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.disabledUrl = "";
        newState.enabled = "";
        newState.macKey = "";
        newState.partnerName = "";
        newState.ssoSiteId = "";
        newState.errors = {};

        this.setState(newState);
    };

    navigateToPreviousPage = () => {
        this.props.history.push('/home/partners');
    };

    navigateToLoginPage = () => {
        localStorage.removeItem('tmcUser')
        this.props.history.push('/');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default PartnerForm;
